// src/pages/partner_management/layout/PartnerManagementUI.tsx
//
// Partner Overview (EE-272): header search + filter bar + sortable, paged
// table, server-side throughout, laid out like RequisitionListUI. The page
// owns every piece of query state, persisted to sessionStorage so leaving and
// coming back restores the list; the table just renders what it is given.
//
// Create/edit/delete go through react-query invalidation of the `partners`
// prefix, which refreshes this list and the supplier dropdowns under
// Outsourcing in one go. The previous `refreshTrigger` counter only reached
// this table, so a deleted partner stayed in the dropdowns for up to ten
// minutes.
import { useContext, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { useQueryClient } from "react-query";
import DashboardHeaderBar from "components/Dashboard/DashboardHeaderBar";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import { partnerKeys, usePartnerList } from "hooks/usePartners";
import { PartnerSortKey, PartnerType } from "types/partners/PartnerTypes";
import { apiDeletePartner } from "util/network/Partners";
import PartnerTable from "../PartnerTable";
import PartnerFilterBar, {
  EMPTY_PARTNER_FILTERS,
  PartnerFilterState,
} from "../PartnerFilterBar";
import CreateOrUpdatePartnerModal from "../CreateOrUpdatePartnerModal";
import DeletePartnerModal from "../DeletePartnerModal";

const PartnerManagementUI = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [searchVal, setSearchVal] = useStateWithSessionStorage<string>(
    "",
    "partnerSearchVal"
  );
  const [filters, setFilters] = useStateWithSessionStorage<PartnerFilterState>(
    EMPTY_PARTNER_FILTERS,
    "partnerFilters"
  );
  const [page, setPage] = useStateWithSessionStorage<number>(0, "partnerPage");
  const [pageSize, setPageSize] = useStateWithSessionStorage<number>(
    25,
    "partnerPageSize"
  );
  const [sortKey, setSortKey] = useStateWithSessionStorage<PartnerSortKey>(
    "id",
    "partnerSortKey"
  );
  const [sortAsc, setSortAsc] = useStateWithSessionStorage<boolean>(
    false,
    "partnerSortAsc"
  );

  const [existing, setExisting] = useState<PartnerType>();
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showDeletePartner, setShowDeletePartner] = useState(false);

  const queryFilters = useMemo(
    () => ({
      search: searchVal || undefined,
      templateType: filters.templateType || undefined,
      templateDelivery: filters.templateDelivery || undefined,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      // TablePagination counts pages from 0, the API from 1.
      page: page + 1,
      pageSize,
      sortKey,
      sortDirection: (sortAsc ? "asc" : "desc") as "asc" | "desc",
    }),
    [searchVal, filters, page, pageSize, sortKey, sortAsc]
  );

  const { data, isFetching, refetch } = usePartnerList(token, queryFilters);

  const invalidatePartners = () =>
    queryClient.invalidateQueries(partnerKeys.all);

  // A narrowed or re-sorted result set is a different list; staying on page 4
  // of it shows rows that may no longer exist.
  const handleSearchChange = (value: string) => {
    setSearchVal(value);
    setPage(0);
  };

  const handleFiltersChange = (next: PartnerFilterState) => {
    setFilters(next);
    setPage(0);
  };

  const handleSortChange = (key: PartnerSortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
    setPage(0);
  };

  const handleDeletePartner = (id: number) => {
    apiDeletePartner(token, id)
      .then(() => {
        addToast({
          type: "success",
          message: (
            <>
              Partner was <strong>deleted</strong> successfully.
            </>
          ),
        });
        invalidatePartners();
      })
      .catch((e) => {
        addToast({
          type: "error",
          message: `Could not delete partner: ${e?.message ?? e}`,
          keep: true,
        });
      })
      .finally(() => {
        setShowDeletePartner(false);
        setExisting(undefined);
      });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <DashboardHeaderBar
          title="Partner overview"
          search={{
            value: searchVal,
            onChange: handleSearchChange,
            placeholder: "Search partners..",
          }}
          action={{
            label: "Add New Partner",
            onClick: () => {
              setExisting(undefined);
              setShowPartnerModal(true);
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ padding: "1rem 1.5rem 0 1.5rem" }}>
        <PartnerFilterBar filters={filters} onChange={handleFiltersChange} />
      </Grid>
      <Grid item xs={12} sx={{ padding: "1.5rem" }}>
        <PartnerTable
          partners={data?.items ?? []}
          total={data?.total ?? 0}
          isFetching={isFetching}
          page={page}
          pageSize={pageSize}
          sortKey={sortKey}
          sortAsc={sortAsc}
          onSortChange={handleSortChange}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(0);
          }}
          onEdit={(partner) => {
            setExisting(partner);
            setShowPartnerModal(true);
          }}
          onDelete={(partner) => {
            setExisting(partner);
            setShowDeletePartner(true);
          }}
          onRefresh={() => refetch()}
        />
      </Grid>
      <CreateOrUpdatePartnerModal
        open={showPartnerModal}
        onClose={() => {
          setShowPartnerModal(false);
          setExisting(undefined);
        }}
        partner={existing}
        onSubmit={() => {
          addToast({
            type: "success",
            message: existing ? (
              <>
                Partner was <strong>edited</strong> successfully.
              </>
            ) : (
              <>
                New partner was <strong>created</strong> successfully.
              </>
            ),
          });
          invalidatePartners();
          setShowPartnerModal(false);
          setExisting(undefined);
        }}
      />
      <DeletePartnerModal
        open={showDeletePartner}
        onClose={() => setShowDeletePartner(false)}
        onSubmit={handleDeletePartner}
        partnerId={existing?.id}
      />
    </Grid>
  );
};

export default PartnerManagementUI;
